/**
 * RecommendationSettings Component
 *
 * Allows users to fine-tune recommendation algorithms, mood profiles,
 * and general tuning parameters.
 *
 * Features progressive disclosure: Basic mode for casual users,
 * Advanced mode for power users.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '@/lib/auth';
import { useToast } from '@/contexts/ToastContext';
import { Collapsible, CollapsibleGroup } from './ui/Collapsible';

interface AlgorithmWeights {
  personalized_mood: number;
  collaborative: number;
  ai_personalized: number;
  exploration: number;
}

interface TuningParameters {
  strictness: number;
  diversity_weight: number;
  exploration_rate: number;
}

interface FeatureFlags {
  enable_ai_recommendations: boolean;
  enable_implicit_feedback: boolean;
  enable_collaborative_filtering: boolean;
}

interface MoodProfileOverride {
  energy_min?: number;
  energy_max?: number;
  valence_min?: number;
  valence_max?: number;
  danceability_min?: number;
  danceability_max?: number;
  acousticness_min?: number;
  acousticness_max?: number;
  instrumentalness_min?: number;
  instrumentalness_max?: number;
  tempo_min?: number;
  tempo_max?: number;
}

interface MoodProfile {
  name: string;
  description: string;
  energy_min: number;
  energy_max: number;
  valence_min: number;
  valence_max: number;
  danceability_min?: number;
  danceability_max?: number;
  acousticness_min?: number;
  acousticness_max?: number;
  instrumentalness_min?: number;
  instrumentalness_max?: number;
  tempo_min?: number;
  tempo_max?: number;
}

interface Settings {
  algorithm_weights: AlgorithmWeights;
  tuning: TuningParameters;
  features: FeatureFlags;
  mood_profile_overrides: Record<string, MoodProfileOverride>;
}

type SettingsMode = 'basic' | 'advanced';

const RecommendationSettings: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [moodProfiles, setMoodProfiles] = useState<Record<string, MoodProfile>>({});
  const [availableMoods, setAvailableMoods] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedMood, setSelectedMood] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [mode, setMode] = useState<SettingsMode>('basic');
  const { success, error: showError } = useToast();

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, moodsRes] = await Promise.all([
        authenticatedFetch('/api/settings/recommendations'),
        authenticatedFetch('/api/settings/mood-profiles'),
      ]);

      if (!settingsRes.ok) throw new Error('Failed to load settings');
      if (!moodsRes.ok) throw new Error('Failed to load mood profiles');

      const settingsData = await settingsRes.json();
      const moodsData = await moodsRes.json();

      setSettings(settingsData);
      setMoodProfiles(moodsData.profiles || {});
      setAvailableMoods(moodsData.available_moods || []);

      if (moodsData.available_moods?.length > 0) {
        setSelectedMood(moodsData.available_moods[0]);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    if (!settings) return;

    setSaving(true);

    try {
      const response = await authenticatedFetch('/api/settings/recommendations', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save settings');
      }

      success('Settings saved!');
      setHasChanges(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    if (!confirm('Are you sure you want to reset all settings to defaults?')) return;

    setSaving(true);

    try {
      const response = await authenticatedFetch('/api/settings/recommendations/reset', {
        method: 'POST',
      });

      if (!response.ok) throw new Error('Failed to reset settings');

      const data = await response.json();
      setSettings(data);
      setHasChanges(false);
      success('Settings reset to defaults');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const updateAlgorithmWeight = (key: keyof AlgorithmWeights, value: number) => {
    if (!settings) return;

    const newWeights = { ...settings.algorithm_weights, [key]: value };

    // Normalize other weights to sum to 1
    const total = Object.values(newWeights).reduce((a, b) => a + b, 0);
    if (total > 0) {
      const otherKeys = Object.keys(newWeights).filter(k => k !== key) as (keyof AlgorithmWeights)[];
      const remaining = 1 - value;
      const otherTotal = otherKeys.reduce((sum, k) => sum + newWeights[k], 0);

      if (otherTotal > 0) {
        otherKeys.forEach(k => {
          newWeights[k] = (newWeights[k] / otherTotal) * remaining;
        });
      }
    }

    setSettings({
      ...settings,
      algorithm_weights: newWeights,
    });
    setHasChanges(true);
  };

  const updateTuning = (key: keyof TuningParameters, value: number) => {
    if (!settings) return;
    setSettings({
      ...settings,
      tuning: { ...settings.tuning, [key]: value },
    });
    setHasChanges(true);
  };

  const updateFeature = (key: keyof FeatureFlags, value: boolean) => {
    if (!settings) return;
    setSettings({
      ...settings,
      features: { ...settings.features, [key]: value },
    });
    setHasChanges(true);
  };

  const updateMoodOverride = (mood: string, key: keyof MoodProfileOverride, value: number | null) => {
    if (!settings) return;

    const currentOverrides = settings.mood_profile_overrides || {};
    const currentMoodOverride = currentOverrides[mood] || {};

    const newMoodOverride = value === null
      ? Object.fromEntries(Object.entries(currentMoodOverride).filter(([k]) => k !== key))
      : { ...currentMoodOverride, [key]: value };

    setSettings({
      ...settings,
      mood_profile_overrides: {
        ...currentOverrides,
        [mood]: newMoodOverride,
      },
    });
    setHasChanges(true);
  };

  const resetMoodOverride = (mood: string) => {
    if (!settings) return;

    const { [mood]: _, ...rest } = settings.mood_profile_overrides || {};
    setSettings({
      ...settings,
      mood_profile_overrides: rest,
    });
    setHasChanges(true);
  };

  const getEffectiveMoodValue = (mood: string, key: keyof MoodProfileOverride): number | undefined => {
    const override = settings?.mood_profile_overrides?.[mood]?.[key];
    if (override !== undefined) return override;
    return moodProfiles[mood]?.[key as keyof MoodProfile] as number | undefined;
  };

  const isOverridden = (mood: string, key: keyof MoodProfileOverride): boolean => {
    return settings?.mood_profile_overrides?.[mood]?.[key] !== undefined;
  };

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-8">
        <div className="text-center text-gray-400">Loading recommendation settings...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-8">
        <div className="text-center text-red-400">Failed to load settings</div>
        <button
          onClick={loadSettings}
          className="mt-4 bg-spotify-green hover:bg-spotify-green-dark text-black px-4 py-2 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white">Recommendation Settings</h2>
            <p className="text-sm text-gray-400 mt-1">Fine-tune how music is recommended to you</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Mode Toggle */}
            <div className="flex bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setMode('basic')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  mode === 'basic'
                    ? 'bg-spotify-green text-black'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Basic
              </button>
              <button
                onClick={() => setMode('advanced')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  mode === 'advanced'
                    ? 'bg-spotify-green text-black'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Advanced
              </button>
            </div>
            <button
              onClick={resetSettings}
              disabled={saving}
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors text-sm"
            >
              Reset
            </button>
            <button
              onClick={saveSettings}
              disabled={saving || !hasChanges}
              className={`px-4 py-2 rounded transition-colors text-sm font-semibold ${
                hasChanges
                  ? 'bg-spotify-green hover:bg-spotify-green-dark text-black'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* BASIC MODE */}
        {mode === 'basic' && (
          <div className="space-y-6">
            {/* Simple Exploration Slider */}
            <div className="bg-gray-800/50 rounded-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-white font-medium">Discovery Level</h4>
                  <p className="text-gray-500 text-sm">How adventurous should your recommendations be?</p>
                </div>
                <span className="text-spotify-green font-mono text-lg">
                  {Math.round(settings.tuning.exploration_rate * 100)}%
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">Familiar</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(settings.tuning.exploration_rate * 100)}
                  onChange={e => updateTuning('exploration_rate', parseInt(e.target.value) / 100)}
                  className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-spotify-green"
                />
                <span className="text-sm text-gray-400">New Music</span>
              </div>
            </div>

            {/* Quick Feature Toggles */}
            <div className="space-y-3">
              <h4 className="text-white font-medium mb-3">Features</h4>
              {[
                {
                  key: 'enable_ai_recommendations' as const,
                  label: 'AI-Powered',
                  desc: 'Smart recommendations based on your taste',
                  icon: '🤖',
                },
                {
                  key: 'enable_implicit_feedback' as const,
                  label: 'Learning Mode',
                  desc: 'Improve over time from your behavior',
                  icon: '📊',
                },
                {
                  key: 'enable_collaborative_filtering' as const,
                  label: 'Social Discovery',
                  desc: 'Find music similar users enjoy',
                  icon: '👥',
                },
              ].map(item => (
                <div
                  key={item.key}
                  className="bg-gray-800/50 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <h4 className="text-white font-medium">{item.label}</h4>
                      <p className="text-gray-500 text-xs">{item.desc}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateFeature(item.key, !settings.features[item.key])}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.features[item.key] ? 'bg-spotify-green' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.features[item.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>

            <p className="text-center text-gray-500 text-sm pt-4">
              Need more control? Switch to <button onClick={() => setMode('advanced')} className="text-spotify-green hover:underline">Advanced mode</button>
            </p>
          </div>
        )}

        {/* ADVANCED MODE */}
        {mode === 'advanced' && (
          <CollapsibleGroup>
            {/* Algorithm Weights */}
            <Collapsible
              title="Algorithm Weights"
              subtitle="Control how each recommendation engine contributes"
              icon="🎯"
              defaultOpen={true}
            >
          <div className="space-y-6">
            <p className="text-gray-400 text-sm mb-6">
              Adjust how much each recommendation algorithm contributes to your music suggestions.
              Weights automatically balance to sum to 100%.
            </p>

            {[
              { key: 'personalized_mood' as const, label: 'Mood-Based', desc: 'Matches music to your selected mood using audio features' },
              { key: 'collaborative' as const, label: 'Collaborative', desc: 'Based on what similar users enjoy' },
              { key: 'ai_personalized' as const, label: 'AI-Powered', desc: 'Uses AI to analyze your listening patterns' },
              { key: 'exploration' as const, label: 'Exploration', desc: 'Discovers new music outside your usual taste' },
            ].map(item => (
              <div key={item.key} className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-white font-medium">{item.label}</h4>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                  <span className="text-spotify-green font-mono text-lg">
                    {Math.round(settings.algorithm_weights[item.key] * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(settings.algorithm_weights[item.key] * 100)}
                  onChange={e => updateAlgorithmWeight(item.key, parseInt(e.target.value) / 100)}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-spotify-green"
                />
              </div>
            ))}
            </div>
            </Collapsible>

            {/* General Tuning */}
            <Collapsible
              title="General Tuning"
              subtitle="Fine-tune recommendation parameters"
              icon="🎛️"
            >
              <div className="space-y-4 pt-4">
                {[
                  {
                    key: 'strictness' as const,
                    label: 'Mood Strictness',
                    desc: 'How strictly to match audio features to mood profiles',
                    leftLabel: 'Loose',
                    rightLabel: 'Exact',
                  },
                  {
                    key: 'diversity_weight' as const,
                    label: 'Diversity',
                    desc: 'Balance between relevance and variety in recommendations',
                    leftLabel: 'Similar',
                    rightLabel: 'Varied',
                  },
                  {
                    key: 'exploration_rate' as const,
                    label: 'Exploration Rate',
                    desc: 'How often to suggest new music vs familiar tracks',
                    leftLabel: 'Familiar',
                    rightLabel: 'New',
                  },
                ].map(item => (
                  <div key={item.key} className="bg-gray-900/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-white font-medium">{item.label}</h4>
                        <p className="text-gray-500 text-xs">{item.desc}</p>
                      </div>
                      <span className="text-spotify-green font-mono text-lg">
                        {Math.round(settings.tuning[item.key] * 100)}%
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-16">{item.leftLabel}</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={Math.round(settings.tuning[item.key] * 100)}
                        onChange={e => updateTuning(item.key, parseInt(e.target.value) / 100)}
                        className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-spotify-green"
                      />
                      <span className="text-xs text-gray-500 w-16 text-right">{item.rightLabel}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Collapsible>

            {/* Mood Profiles */}
            <Collapsible
              title="Mood Profiles"
              subtitle="Customize audio features for each mood"
              icon="🎨"
            >
              <div className="space-y-4 pt-4">
                {/* Mood Selector */}
                <div className="flex flex-wrap gap-2">
                  {availableMoods.map(mood => {
                    const hasOverrides = Object.keys(settings.mood_profile_overrides?.[mood] || {}).length > 0;
                    return (
                      <button
                        key={mood}
                        onClick={() => setSelectedMood(mood)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          selectedMood === mood
                            ? 'bg-spotify-green text-black'
                            : hasOverrides
                            ? 'bg-purple-900/50 text-purple-300 border border-purple-700'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {moodProfiles[mood]?.name || mood}
                        {hasOverrides && selectedMood !== mood && <span className="ml-1">*</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Selected Mood Settings */}
                {selectedMood && moodProfiles[selectedMood] && (
                  <div className="bg-gray-900/50 rounded-lg p-4 mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-white font-medium">{moodProfiles[selectedMood].name}</h4>
                        <p className="text-gray-500 text-xs">{moodProfiles[selectedMood].description}</p>
                      </div>
                      {Object.keys(settings.mood_profile_overrides?.[selectedMood] || {}).length > 0 && (
                        <button
                          onClick={() => resetMoodOverride(selectedMood)}
                          className="text-xs text-gray-400 hover:text-white"
                        >
                          Reset
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        { key: 'energy', label: 'Energy', icon: '⚡' },
                        { key: 'valence', label: 'Valence', icon: '😊' },
                        { key: 'danceability', label: 'Danceability', icon: '💃' },
                        { key: 'acousticness', label: 'Acousticness', icon: '🎸' },
                        { key: 'instrumentalness', label: 'Instrumentalness', icon: '🎹' },
                      ].map(feature => {
                        const minKey = `${feature.key}_min` as keyof MoodProfileOverride;
                        const maxKey = `${feature.key}_max` as keyof MoodProfileOverride;
                        const minValue = getEffectiveMoodValue(selectedMood, minKey);
                        const maxValue = getEffectiveMoodValue(selectedMood, maxKey);
                        const isMinOverridden = isOverridden(selectedMood, minKey);
                        const isMaxOverridden = isOverridden(selectedMood, maxKey);

                        return (
                          <div key={feature.key} className="bg-gray-800 rounded p-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-gray-300 text-xs">
                                {feature.icon} {feature.label}
                              </span>
                              {(isMinOverridden || isMaxOverridden) && (
                                <span className="text-purple-400 text-xs">*</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max="1"
                                step="0.05"
                                value={minValue ?? ''}
                                onChange={e => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  updateMoodOverride(selectedMood, minKey, val);
                                }}
                                placeholder="min"
                                className={`w-full bg-gray-700 border rounded px-2 py-1 text-xs ${
                                  isMinOverridden ? 'border-purple-500 text-purple-300' : 'border-gray-600 text-white'
                                }`}
                              />
                              <span className="text-gray-600 text-xs">-</span>
                              <input
                                type="number"
                                min="0"
                                max="1"
                                step="0.05"
                                value={maxValue ?? ''}
                                onChange={e => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  updateMoodOverride(selectedMood, maxKey, val);
                                }}
                                placeholder="max"
                                className={`w-full bg-gray-700 border rounded px-2 py-1 text-xs ${
                                  isMaxOverridden ? 'border-purple-500 text-purple-300' : 'border-gray-600 text-white'
                                }`}
                              />
                            </div>
                          </div>
                        );
                      })}

                      {/* Tempo */}
                      <div className="bg-gray-800 rounded p-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-300 text-xs">🎵 Tempo (BPM)</span>
                          {(isOverridden(selectedMood, 'tempo_min') || isOverridden(selectedMood, 'tempo_max')) && (
                            <span className="text-purple-400 text-xs">*</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="30"
                            max="250"
                            step="5"
                            value={getEffectiveMoodValue(selectedMood, 'tempo_min') ?? ''}
                            onChange={e => {
                              const val = e.target.value === '' ? null : parseFloat(e.target.value);
                              updateMoodOverride(selectedMood, 'tempo_min', val);
                            }}
                            placeholder="min"
                            className={`w-full bg-gray-700 border rounded px-2 py-1 text-xs ${
                              isOverridden(selectedMood, 'tempo_min') ? 'border-purple-500 text-purple-300' : 'border-gray-600 text-white'
                            }`}
                          />
                          <span className="text-gray-600 text-xs">-</span>
                          <input
                            type="number"
                            min="30"
                            max="250"
                            step="5"
                            value={getEffectiveMoodValue(selectedMood, 'tempo_max') ?? ''}
                            onChange={e => {
                              const val = e.target.value === '' ? null : parseFloat(e.target.value);
                              updateMoodOverride(selectedMood, 'tempo_max', val);
                            }}
                            placeholder="max"
                            className={`w-full bg-gray-700 border rounded px-2 py-1 text-xs ${
                              isOverridden(selectedMood, 'tempo_max') ? 'border-purple-500 text-purple-300' : 'border-gray-600 text-white'
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Collapsible>

            {/* Features */}
            <Collapsible
              title="Feature Flags"
              subtitle="Enable or disable recommendation features"
              icon="⚙️"
            >
              <div className="space-y-3 pt-4">
                {[
                  {
                    key: 'enable_ai_recommendations' as const,
                    label: 'AI-Powered Recommendations',
                    desc: 'Use AI to analyze your listening patterns',
                  },
                  {
                    key: 'enable_implicit_feedback' as const,
                    label: 'Implicit Feedback Learning',
                    desc: 'Learn from skips, completion rate, replays',
                  },
                  {
                    key: 'enable_collaborative_filtering' as const,
                    label: 'Collaborative Filtering',
                    desc: 'Recommend based on similar users',
                  },
                ].map(item => (
                  <div
                    key={item.key}
                    className="bg-gray-900/50 rounded-lg p-3 flex items-center justify-between"
                  >
                    <div>
                      <h4 className="text-white font-medium text-sm">{item.label}</h4>
                      <p className="text-gray-500 text-xs">{item.desc}</p>
                    </div>
                    <button
                      onClick={() => updateFeature(item.key, !settings.features[item.key])}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.features[item.key] ? 'bg-spotify-green' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.features[item.key] ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </Collapsible>
          </CollapsibleGroup>
        )}
      </div>
    </div>
  );
};

export default RecommendationSettings;
